/**
 * Service Worker Registration
 *
 * Replaces the `registerSW.js` snippet that vite-plugin-pwa injects by default
 * (`injectRegister` is set to `null` in vite.config.ts). That snippet called
 * `navigator.serviceWorker.register()` without a rejection handler, so a failed
 * registration became an unhandled promise rejection and was reported to Sentry
 * as an unhandled error.
 *
 * Registration failing is expected in the wild: Safari drops the script request
 * on flaky connections, and embedded/in-app browsers refuse to register service
 * workers at all. The app is fully usable without one — it only loses offline
 * precaching — so a failure is logged rather than left to bubble up.
 */

import { logger } from './logger';

export function registerServiceWorker(): void {
  // `sw.js` is only emitted by `vite build`, so skip the dev server.
  if (!import.meta.env.PROD) {
    return;
  }

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(error => {
      logger.warn(
        'Service worker registration failed, continuing without offline support',
        error instanceof Error ? error : undefined,
        {
          component: 'registerServiceWorker',
          operation: 'register',
        }
      );
    });
  });
}

export default registerServiceWorker;
